<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: CollapsiblePanel                                      |
| Section: components                                              |
| Purpose: Expandable panel with animated open-close transitions.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="collapsible__wrapper" :class="{ 'collapsible_ancestors': isAncestorTree }">
        <div class="collapsible__item">
            <h3 class="collapsible__button" @click="toggleCollapsible">
                {{ label }}
                <div class="collapsible__arrow" :style="{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)' }">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                        <path
                            d="M5.29414 13.7063C5.68477 14.0969 6.31914 14.0969 6.70977 13.7063L11.7098 8.70625C12.1004 8.31563 12.1004 7.68125 11.7098 7.29063C11.3191 6.9 10.6848 6.9 10.2941 7.29063L7.00039 10.5875V1C7.00039 0.446875 6.55352 0 6.00039 0C5.44727 0 5.00039 0.446875 5.00039 1V10.5844L1.70664 7.29375C1.31602 6.90312 0.681641 6.90312 0.291016 7.29375C-0.0996094 7.68437 -0.0996094 8.31875 0.291016 8.70938L5.29102 13.7094L5.29414 13.7063Z"
                            :fill="arrowColor" />
                    </svg>
                </div>
            </h3>
            <div class="collapsible__content"
                :style="{ maxHeight: isActive ? contentHeight + 'px' : '0px', paddingBottom: isActive ? '24px' : '0' }"
                ref="collapsibleContent">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        label: {
            type: String,
            required: true,
        },
        arrowColor: {
            type: String,
            default: '#FFF',
        },
        isAncestorTree: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isActive: false, // Tracks whether the collapsible is open
            contentHeight: 0, // Stores the calculated height of the content
        };
    },
    mounted() {
        this.updateContentHeight(); // Set the initial content height

        // Observe changes in the collapsible content
        const contentElement = this.$refs.collapsibleContent;
        if (contentElement) {
            this.mutationObserver = new MutationObserver(() => {
                if (this.isActive) {
                    this.updateContentHeight(); // Update height when content changes
                }
            });

            this.mutationObserver.observe(contentElement, {
                childList: true, // Observe direct children
                subtree: true,   // Observe all descendants
                characterData: true, // Observe text changes
            });
        }
    },
    beforeUnmount() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    },
    watch: {
        isActive(newValue) {
            if (newValue) {
                this.updateContentHeight(); // Recalculate height when the panel is expanded
            }
        },
    },
    methods: {
        toggleCollapsible() {
            this.isActive = !this.isActive;
            if (this.isActive) {
                this.updateContentHeight(); // Update height when opened
            }
        },
        updateContentHeight() {
            const contentElement = this.$refs.collapsibleContent;
            if (contentElement) {
                this.contentHeight = contentElement.scrollHeight; // Calculate the height of the content
            }
        },
    },
};
</script>

<style scoped>
.collapsible__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    gap: 24px;
    margin-top: 4px;
}

.collapsible__button {
    box-sizing: border-box;
    cursor: pointer;
    padding: 24px 0;
    width: 100%;
    border: none;
    text-align: left;
    outline: none;
    transition: all 0.25s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.collapsible__button:hover {
    text-decoration: underline;
}

.collapsible__arrow {
    /* width: 24px; */
    height: auto;
    transform: rotate(0deg);
    transition: all 0.25s ease;
}

.collapsible__content {
    overflow: hidden;
    max-height: 0;
    transition: all 0.25s ease;
    color: var(--brand-text-light);
}

/* ancestor styling */

.collapsible_ancestors {
    margin-top: 0;
    padding: 0;
}

.collapsible_ancestors .collapsible__button {
    font-size: 14px;
    font-weight: 900;
    text-transform: uppercase;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.collapsible_ancestors .collapsible__content {
    color: var(--brand-text-dark);
}
</style>